import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
import { getAdminHomePath, getDefaultHomePath, PERM } from '@/utils/adminPermission'

export function getHomePath(role, adminRoleCode, adminPermissions) {
  return getDefaultHomePath(
    role ?? store.state.role,
    adminRoleCode ?? store.state.adminRoleCode,
    adminPermissions ?? store.state.adminPermissions
  )
}

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true }
  },
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' },
    redirect: () => getAdminHomePath(store.state.adminPermissions),
    children: [
      {
        path: 'templates',
        name: 'admin-templates',
        component: () => import('@/views/admin/TemplateListView.vue'),
        meta: { permission: PERM.TEMPLATE }
      },
      {
        path: 'templates/design/:id',
        name: 'admin-template-design',
        component: () => import('@/views/admin/FlowDesigner.vue'),
        meta: { permission: PERM.TEMPLATE }
      },
      {
        path: 'depts',
        name: 'admin-depts',
        component: () => import('@/views/admin/DeptManage.vue'),
        meta: { permission: PERM.DEPT }
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/views/admin/UserManage.vue'),
        meta: { permission: PERM.USER }
      },
      {
        path: 'posts',
        name: 'admin-posts',
        component: () => import('@/views/admin/PostManage.vue'),
        meta: { permission: PERM.POST }
      },
      {
        path: 'groups',
        name: 'admin-groups',
        component: () => import('@/views/admin/GroupManage.vue'),
        meta: { permission: PERM.GROUP }
      },
      {
        path: 'roles',
        name: 'admin-roles',
        component: () => import('@/views/admin/RoleManage.vue'),
        meta: { permission: PERM.ROLE }
      },
      {
        path: 'instances',
        name: 'admin-instances',
        component: () => import('@/views/admin/InstanceMonitor.vue'),
        meta: { permission: PERM.OPS }
      },
      {
        path: 'instances/:id',
        name: 'admin-instance-detail',
        component: () => import('@/views/admin/InstanceDetail.vue'),
        meta: { permission: PERM.OPS }
      },
      {
        path: 'schedules',
        name: 'admin-schedules',
        component: () => import('@/views/admin/ScheduleManage.vue'),
        meta: { permission: PERM.SCHEDULE }
      },
      {
        path: 'reports',
        redirect: '/admin/instances'
      },
      {
        path: 'audit-logs',
        name: 'admin-audit-logs',
        component: () => import('@/views/admin/AuditLogView.vue'),
        meta: { permission: PERM.AUDIT }
      },
      {
        path: 'dicts',
        name: 'admin-dicts',
        component: () => import('@/views/admin/DictManage.vue'),
        meta: { permission: PERM.DICT }
      },
      {
        path: 'office-supplies',
        name: 'admin-office-supplies',
        component: () => import('@/views/admin/OfficeSupplyManage.vue'),
        meta: { permission: PERM.OFFICE_SUPPLY }
      },
      {
        path: 'profile',
        name: 'admin-profile',
        component: () => import('@/views/profile/ProfileView.vue')
      },
      {
        path: 'portal/articles',
        name: 'admin-portal-articles',
        component: () => import('@/views/admin/portal/PortalArticleManage.vue'),
        meta: { permission: PERM.PORTAL_CONTENT }
      },
      {
        path: 'portal/review',
        name: 'admin-portal-review',
        component: () => import('@/views/admin/portal/PortalReviewManage.vue'),
        meta: { permission: PERM.PORTAL_REVIEW }
      },
      {
        path: 'portal/home-design',
        name: 'admin-portal-home-design',
        component: () => import('@/views/admin/portal/PortalHomeDesigner.vue'),
        meta: { permission: PERM.PORTAL_DESIGN }
      }
    ]
  },
  {
    path: '/workspace',
    component: () => import('@/layouts/WorkspaceLayout.vue'),
    meta: { requiresAuth: true },
    redirect: '/workspace/home',
    children: [
      {
        path: 'home',
        name: 'workspace-home',
        component: () => import('@/views/workspace/PortalHomeView.vue')
      },
      {
        path: 'apply',
        name: 'workspace-apply',
        component: () => import('@/views/workspace/Apply.vue')
      },
      {
        path: 'free-collab',
        name: 'workspace-free-collab',
        component: () => import('@/views/workspace/FreeCollab.vue')
      },
      {
        path: 'pending',
        name: 'workspace-pending',
        component: () => import('@/views/workspace/Pending.vue')
      },
      {
        path: 'tasks/:taskId/process',
        name: 'workspace-task-process',
        component: () => import('@/views/workspace/TaskProcess.vue')
      },
      {
        path: 'suspended',
        name: 'workspace-suspended',
        component: () => import('@/views/workspace/SuspendedTasks.vue')
      },
      {
        path: 'handled',
        name: 'workspace-handled',
        component: () => import('@/views/workspace/HandledTasks.vue')
      },
      {
        path: 'cc',
        name: 'workspace-cc',
        component: () => import('@/views/workspace/CcList.vue')
      },
      {
        path: 'instances/:id',
        name: 'workspace-instance-detail',
        component: () => import('@/views/workspace/InstanceDetail.vue')
      },
      {
        path: 'my-requests',
        name: 'workspace-my-requests',
        component: () => import('@/views/workspace/MyRequests.vue')
      },
      {
        path: 'delegates',
        name: 'workspace-delegates',
        component: () => import('@/views/workspace/DelegateSettings.vue')
      },
      {
        path: 'data-reports',
        name: 'workspace-data-reports',
        component: () => import('@/views/workspace/DataReportView.vue')
      },
      {
        path: 'data-reports/:templateId/:reportId',
        name: 'workspace-data-report-detail',
        component: () => import('@/views/workspace/DataReportView.vue')
      },
      {
        path: 'data-reports/:templateId',
        name: 'workspace-data-report-legacy',
        component: () => import('@/views/workspace/DataReportView.vue')
      },
      {
        path: 'portal/list',
        name: 'workspace-portal-list',
        component: () => import('@/views/workspace/PortalArticleListView.vue')
      },
      {
        path: 'portal/articles/:id',
        name: 'workspace-portal-article',
        component: () => import('@/views/workspace/PortalArticleView.vue')
      },
      {
        path: 'profile',
        name: 'workspace-profile',
        component: () => import('@/views/profile/ProfileView.vue')
      }
    ]
  },
  {
    path: '/m/evidence/:id/hub',
    name: 'mobile-evidence-hub',
    component: () => import('@/views/mobile/EvidenceHub.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/m/evidence/:id',
    name: 'mobile-evidence',
    component: () => import('@/views/mobile/EvidenceUpload.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    redirect: '/login'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to, from, next) => {
  const token = store.state.token
  const role = store.state.role
  const adminPermissions = store.state.adminPermissions

  if (to.meta.public) {
    if (token && to.path === '/login') {
      next(getHomePath(role, store.state.adminRoleCode, adminPermissions))
      return
    }
    next()
    return
  }

  if (!token) {
    next({
      path: '/login',
      query: to.path === '/login' ? {} : { redirect: to.fullPath }
    })
    return
  }

  if (to.meta.role && to.meta.role !== role) {
    next(getHomePath(role, store.state.adminRoleCode, adminPermissions))
    return
  }

  if (to.meta.permission && !store.getters.hasAdminPermission(to.meta.permission)) {
    next(getHomePath(role, store.state.adminRoleCode, adminPermissions))
    return
  }

  next()
})

export default router
